import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import { InputText } from 'primereact/inputtext';
import { classNames } from 'primereact/utils';
import { Product } from '../../../types/product';
import { EditablePurchaseOrderItem, EditableHandlingUnit, ItemFieldErrors } from './types';
import { createNewItem } from './utils';
import ItemEditor from './ItemEditor';

interface ItemsTableProps {
  items: EditablePurchaseOrderItem[];
  purchaseOrderId: number;
  filteredProducts: Product[];
  newHU: { huNumber?: string };
  /** Per-item field errors keyed by item index (only populated after a save attempt). */
  itemErrors: Record<number, ItemFieldErrors>;
  /** Increments each time the user attempts an invalid save, to trigger expand + scroll. */
  focusErrorsNonce: number;
  onUpdateItem: (index: number, field: keyof EditablePurchaseOrderItem | 'material', value: any) => void;
  onRemoveItem: (index: number) => void;
  onAddItem: (item: EditablePurchaseOrderItem) => void;
  onSearchProducts: (event: { query: string }) => Promise<void>;
  onUpdateHandlingUnit: (itemIndex: number, huIndex: number, field: keyof EditableHandlingUnit, value: any) => void;
  onRemoveHandlingUnit: (itemIndex: number, huIndex: number) => void;
  onAddHandlingUnit: (itemIndex: number) => void;
  onNewHUChange: (huNumber: string) => void;
}

const hasFieldErrors = (errs?: ItemFieldErrors): boolean =>
  !!errs && Object.values(errs).some(Boolean);

const ItemsTable: React.FC<ItemsTableProps> = ({
  items,
  purchaseOrderId,
  filteredProducts,
  newHU,
  itemErrors,
  focusErrorsNonce,
  onUpdateItem,
  onRemoveItem,
  onAddItem,
  onSearchProducts,
  onUpdateHandlingUnit,
  onRemoveHandlingUnit,
  onAddHandlingUnit,
  onNewHUChange
}) => {
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [filter, setFilter] = useState('');
  const cardRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const handleAddItem = () => {
    const newIndex = items?.length || 0;
    const newItem = createNewItem(newIndex, purchaseOrderId);
    onAddItem(newItem);
    // Auto-expand the newly added item so the user can fill it in.
    setExpanded(prev => new Set(prev).add(newIndex));
  };

  const toggleExpanded = (index: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  // When the user attempts an invalid save, expand every invalid line and
  // scroll the first one into view.
  useEffect(() => {
    if (focusErrorsNonce === 0) return;
    const invalidIndices = (items || [])
      .map((_, i) => i)
      .filter(i => hasFieldErrors(itemErrors[i]));
    if (invalidIndices.length === 0) return;

    setExpanded(prev => {
      const next = new Set(prev);
      invalidIndices.forEach(i => next.add(i));
      return next;
    });

    // Scroll after the expansion has rendered.
    const first = invalidIndices[0];
    window.setTimeout(() => {
      cardRefs.current[first]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const firstInput = cardRefs.current[first]?.querySelector<HTMLElement>('.p-invalid, input');
      firstInput?.focus();
    }, 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusErrorsNonce]);

  const normalizedFilter = filter.trim().toLowerCase();
  const matchesFilter = (item: EditablePurchaseOrderItem): boolean => {
    if (!normalizedFilter) return true;
    const itemNumber = (item.itemNumber || '').toLowerCase();
    const productName = (item.product?.name || '').toLowerCase();
    const material = (item.material || '').toLowerCase();
    return (
      itemNumber.includes(normalizedFilter) ||
      productName.includes(normalizedFilter) ||
      material.includes(normalizedFilter)
    );
  };

  const list = items || [];
  const visible = list
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => matchesFilter(item));

  return (
    <div className="field">
      <div className="flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <label className="font-semibold text-lg m-0">
          Items ({list.length})
        </label>
        <div className="flex align-items-center gap-2">
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter by item # or material"
              className="p-inputtext-sm"
            />
          </span>
          <Button
            label="Add Item"
            icon="pi pi-plus"
            onClick={handleAddItem}
            className="p-button-sm"
            tooltip="Add New Item"
          />
        </div>
      </div>

      {list.length === 0 && (
        <div className="surface-100 border-round p-4 text-center text-color-secondary mb-3">
          No items yet. Click "Add Item" to create the first line.
        </div>
      )}

      {list.length > 0 && visible.length === 0 && (
        <div className="surface-100 border-round p-4 text-center text-color-secondary mb-3">
          No items match "{filter}".
        </div>
      )}

      {visible.map(({ item, index }) => {
        const isExpanded = expanded.has(index);
        const invalid = hasFieldErrors(itemErrors[index]);
        const materialLabel = item.product?.name || item.material || '—';
        const huCount = (item.handlingUnits || []).length;

        return (
          <div
            key={index}
            ref={(el) => { cardRefs.current[index] = el; }}
            className={classNames('border-1 border-round mb-2 surface-card', {
              'surface-border': !invalid,
              'border-red-400': invalid
            })}
          >
            {/* Summary row (clickable) */}
            <div
              className="flex align-items-center gap-3 p-3 cursor-pointer"
              onClick={() => toggleExpanded(index)}
              role="button"
              aria-expanded={isExpanded}
            >
              <i className={classNames('pi', isExpanded ? 'pi-chevron-down' : 'pi-chevron-right')} />
              <span className="font-semibold" style={{ minWidth: '3rem' }}>
                #{item.itemNumber || '—'}
              </span>
              <span className="flex-1 text-overflow-ellipsis overflow-hidden white-space-nowrap">
                {materialLabel}
              </span>
              <span className="text-color-secondary text-sm hidden md:inline">
                Qty: {item.quantity ?? '—'}
              </span>
              <span className="text-color-secondary text-sm hidden md:inline">
                {huCount} HU{huCount === 1 ? '' : 's'}
              </span>
              {invalid && (
                <span className="text-red-500 text-sm flex align-items-center gap-1">
                  <i className="pi pi-exclamation-circle" />
                  <span className="hidden md:inline">Needs attention</span>
                </span>
              )}
              {huCount > 0 && (
                <Badge value={huCount} severity="info" />
              )}
              <Button
                icon="pi pi-trash"
                className="p-button-danger p-button-text p-button-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveItem(index);
                }}
                tooltip="Remove Item"
                tooltipOptions={{ position: 'top' }}
              />
            </div>

            {/* Expanded editor */}
            {isExpanded && (
              <div className="border-top-1 surface-border">
                <ItemEditor
                  item={item}
                  index={index}
                  filteredProducts={filteredProducts}
                  errors={itemErrors[index]}
                  newHU={newHU}
                  onUpdateItem={onUpdateItem}
                  onSearchProducts={onSearchProducts}
                  onUpdateHandlingUnit={onUpdateHandlingUnit}
                  onRemoveHandlingUnit={onRemoveHandlingUnit}
                  onAddHandlingUnit={onAddHandlingUnit}
                  onNewHUChange={onNewHUChange}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ItemsTable;
